import type { TopDownItemId } from './config';

export interface Position {
    top: string;
    left: string;
}

export interface FocusPlacement {
    top?: string;
    left?: string;
    width?: string;
    rotate?: string;
    z?: number;
}

/**
 * Compute offscreen position for an item (toward nearest horizontal edge)
 */
export function computeOffscreen(original: Position): Position {
    const t = parseFloat(original.top);
    const l = parseFloat(original.left);
    const dist = {
        left: l,
        right: 45 - l,
    };
    const edge = Object.entries(dist).sort((a, b) => a[1] - b[1])[0][0];
    if (edge === 'left') return { top: `${t}%`, left: `-50%` };
    return { top: `${t}%`, left: `100%` };
}

/**
 * Compute scatter positions for all items when a specific item is focused
 * @param positions - Map of all item positions
 * @param focusedId - ID of the focused item (if any)
 * @returns Map of item IDs to their scatter positions
 */
export function computeScatterPositions(
    positions: Map<TopDownItemId, Position>,
    focusedId: TopDownItemId | null
): Map<TopDownItemId, Position> {
    const scatterPositions = new Map<TopDownItemId, Position>();
    
    positions.forEach((pos, id) => {
        if (focusedId && id === focusedId) {
            // Focused item keeps its original position (will be overridden by focusPlacement if provided)
            scatterPositions.set(id, pos);
        } else {
            // All other items scatter offscreen
            scatterPositions.set(id, computeOffscreen(pos));
        }
    });
    
    return scatterPositions;
}

/**
 * Apply focus placement overrides to a position
 * @param original - Original position
 * @param placement - Optional focus placement overrides
 * @returns Position with placement applied
 */
export function applyFocusPlacement(
    original: Position,
    placement?: FocusPlacement
): Position {
    if (!placement) return original;
    
    return {
        top: placement.top ?? original.top,
        left: placement.left ?? original.left,
    };
}

